/* ExposureViewRenderer.js */

"use strict";
const FileName = require("@gluenetworks/steve_utilities").fileName;
const Registrations = require("../../../../../RegistrationNamespaces");
const QualifyCvesLauncher = require("../../../../lib/QualifyCvesLauncher");
const ExposureDispositions = require("./ExposureDispositions");
const {computeDependencyFreshness, parseHealth, formatAgeShort} = require("./ExposureFreshness");
const ExposureNavMemory = require("./ExposureNavMemory");

const NO_QUALIFICATION_MESSAGE =
    "No CVE qualification has run for this device yet — use Qualify CVE Exposure in the Devices grid.";
const LOAD_ERROR_MESSAGE = "Unable to load CVE qualification data for this device.";

const EXPOSURE_ACTIVITY_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const NOT_AUTHORIZED_MESSAGE = 'User not authorized for "View Device Vendor Support Data"';

// Summary strip uses the disposition vocabulary ('N Affected · N Potentially
// Affected · …'); the `score` token keeps the existing chip coloring.
const SUMMARY_ITEMS = [
    {key: "redCount", label: "Affected", score: "red"},
    {key: "yellowCount", label: "Potentially Affected", score: "yellow"},
    {key: "greenCount", label: "Not Affected", score: "green"},
    {key: "inconclusiveCount", label: "Inconclusive", score: "inconclusive"},
    {key: "criticalActiveCount", label: "Critical Active", score: "critical-active"},
];

const TABLE_HEADERS = ["CVE", "Disposition", "Decided on", "Severity", "Description", "Reason"];

// Cap for the advisory summary carried in the Description tooltip. Long enough to say what the
// vulnerability is, short enough that the tooltip stays a tooltip.
const TOOLTIP_SUMMARY_LIMIT = 600;

// Cisco SIR ordering, used to rank severity in the qualification table.
const SEVERITY_RANK = {critical: 4, high: 3, medium: 2, low: 1};

// Default table ordering: Affected first, then Potentially Affected,
// Inconclusive, Not Affected; within a disposition, higher severity first.
const DISPOSITION_SORT_RANK = {red: 0, yellow: 1, inconclusive: 2, green: 3};

class ExposureViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("exposure-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;
        this._defaultViewActive = false;
        this._viewActionsAdded = false;
        this._fileNamer = FileName;
        // The root element is the scroll container (see the layout styles), so its offset is the
        // list position worth remembering across a trip into the drawer.
        this._rootElem.addEventListener(
            "scroll",
            (this._onScroll = this._onScroll.bind(this)),
            // Passive: this handler only records the offset, so it must never be allowed to hold
            // up the scroll it is observing.
            {passive: true}
        );
        this._onExposureChanged = this._onExposureChanged.bind(this);
        if (typeof this._model.on === "function") {
            this._model.on("ExposureLifecycleChanged", this._onExposureChanged);
            this._model.on("ExposureVerdictsChanged", this._onExposureChanged);
        }
    }

    _onExposureChanged() {
        if (!this._rootElem) {
            return;
        }
        this.render().catch((err) => {
            this._logger.warn({error: err}, "ExposureViewRenderer live refresh failed.");
        });
    }

    /**
     * Records the list offset as the operator scrolls, so the position is already remembered
     * however they leave the view — the breadcrumb into a CVE, the Exposure tab in the cabinet, or
     * a jump straight to another view.
     */
    _onScroll() {
        ExposureNavMemory.remember(this._model.getDeviceId(), {
            scrollTop: this._rootElem.scrollTop,
        });
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    /**
     * Synchronous placeholder shown for the duration of the model load.
     *
     * Deliberately NOT routed through showDefaultMessageView: that is a promise-returning swap of
     * the whole editor pane, so on a fast tenant it can resolve after the data does and paint
     * "Loading" over a table that is already drawn. Writing into _rootElem is immediate, and
     * _renderQualification empties _rootElem before it builds, so the placeholder cannot survive
     * the render that replaces it.
     */
    _showLoadingDisplay() {
        while (this._rootElem.firstChild) {
            this._rootElem.removeChild(this._rootElem.firstChild);
        }
        const loading = document.createElement("exposure-loading");
        // aria-live so a screen reader announces the wait instead of sitting on a silent pane;
        // "polite" because it must not interrupt whatever the user is currently reading.
        loading.setAttribute("role", "status");
        loading.setAttribute("aria-live", "polite");
        loading.textContent = "Loading exposure results…";
        this._rootElem.appendChild(loading);
    }

    _showDefaultMessageDisplay(message) {
        return this._model.gameEditor
            .showDefaultMessageView({message: message})
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ExposureViewRenderer._showDefaultMessageDisplay error"
                );
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._removeViewActions();
        if (typeof this._model.removeListener === "function") {
            this._model.removeListener("ExposureLifecycleChanged", this._onExposureChanged);
            this._model.removeListener("ExposureVerdictsChanged", this._onExposureChanged);
        }
        this._rootElem.removeEventListener("scroll", this._onScroll);
        return Promise.resolve().then(
            () => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._defaultViewActive = undefined;
                this._viewActionsAdded = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ExposureViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering.
     */
    render() {
        // Something on screen while the qualification, advisories and rules load (D9). This view
        // issues several round-trips before it can draw, and until now it left the previous view's
        // pane up the whole time — on a slow tenant that is indistinguishable from a device with
        // no exposure data, which is the one conclusion a security surface must never invite by
        // accident.
        this._showLoadingDisplay();
        return this._model
            .whenModelIsReady()
            .then(() => {
                const qualification = this._model.getQualification();

                if (!qualification) {
                    return this._showDefaultMessageDisplay(NO_QUALIFICATION_MESSAGE);
                }
                return this._renderQualification(qualification.getPristineProperties()).then(() => {
                    this._addViewActions();
                });
            })
            .catch((err) => {
                this._logger.warn({error: err}, "ExposureViewRenderer.render() warning");
                if (err && err.errorCode === "NotAuthorized") {
                    return this._showDefaultMessageDisplay(NOT_AUTHORIZED_MESSAGE);
                }
                return this._showDefaultMessageDisplay(LOAD_ERROR_MESSAGE);
            });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _addViewActions() {
        if (!this._viewActionsAdded) {
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Qualify",
                name: "QualifyExposure",
                handler: (this._onQualifyExposure = this._onQualifyExposure.bind(this)),
            });
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Download",
                name: "DownloadExposure",
                handler: (this._onDownloadExposure = this._onDownloadExposure.bind(this)),
            });
            this._viewActionsAdded = true;
        }
    }

    /**
     * Re-runs the qualifyCves work scoped to THIS device via the shared
     * "schedule of 1" launcher (same UX as the Devices-grid action). The run
     * executes asynchronously — progress and results are visible under
     * Schedules and in the device Activity tab; re-open or revisit this tab
     * after completion for the refreshed verdicts.
     */
    _onQualifyExposure() {
        const gameEditor = this.model.gameEditor;
        const device = gameEditor.getDevice();
        gameEditor.disableViewAction("QualifyExposure");
        return QualifyCvesLauncher.launchQualifyCves({
            TriggerTypeEntity: this.model.getTriggerTypeEntity(),
            orgId: this.model.getActiveOrgId(),
            workTarget: {
                method: "multi",
                category: "entity",
                entityType: "Device",
                ids: [device.getId()],
            },
            sourceLabel: 'the Exposure tab of "' + (device.name || device.getId()) + '"',
            modalUserFeedback: this.model.getUserFeedback(),
            logger: this._logger,
        }).then(() => {
            // The schedule-of-1 run completes asynchronously — no reload here;
            // the launcher's notification points at Schedules / Activity.
            gameEditor.enableViewAction("QualifyExposure");
        });
    }

    _removeViewActions() {
        if (this._viewActionsAdded) {
            this.model.gameEditor.clearViewActions();
        }
    }

    _onDownloadExposure() {
        this.model.gameEditor.disableViewAction("DownloadExposure");
        const deviceDetails = this.model.gameEditor.getDevice(),
            qualification = this.model.getQualification().getPristineProperties(),
            advisoriesById = this.model.getAdvisoriesById() || new Map(),
            rulesByAdvisoryId = this.model.getRulesByAdvisoryId() || new Map(),
            fileName = this._fileNamer({
                solution: "DM",
                general: "Exposure",
                specific: deviceDetails.name,
                suffix: "csv",
            });
        const printdata = [];
        (qualification.qualifications || []).forEach((row) => {
            const advisory = advisoriesById.get(row.advisoryId) || {};
            const extobj = {};
            // Column order follows the canonical evidence order used by the grid, the hover popup
            // and the drawer (D8): verdict → why → what decided it → what we could NOT establish →
            // what we DID match → advisory facts → audit trail. Negative evidence precedes matched
            // tags everywhere because it is the part an operator has to act on; matched tags only
            // confirm a verdict already stated in the Disposition column.
            extobj["CVE ID"] = row.cveId;
            extobj.Score = row.score;
            extobj.Disposition = ExposureDispositions.dispositionLabel(row.score, row.reasonCode);
            extobj.Severity = advisory.sir;
            extobj["Reason Code"] = row.reasonCode;
            extobj.Reason = row.reasonText;
            // Raw + human, the same pairing as Score/Disposition: the enum stays exportable for
            // spreadsheet filtering, the label is what a reader understands.
            extobj["Decided On"] = row.decidedAt;
            extobj["Deciding Level"] = ExposureDispositions.decidedAtLabel(row.decidedAt);
            extobj["Version Check"] = row.versionCheck
                ? row.versionCheck.result +
                  (row.versionCheck.source ? " (" + row.versionCheck.source + ")" : "")
                : "";
            extobj["Hardware Check"] = row.platformCheck
                ? row.platformCheck.result +
                  (row.platformCheck.cause ? ": " + row.platformCheck.cause : "") +
                  " (" +
                  row.platformCheck.source +
                  ")"
                : "";
            // Missing Tags stays for continuity with saved exports and with verdicts scored before
            // the split. The two columns around it say what the absence MEANS, which the flat list
            // cannot: not-configured is evidence, cannot-verify is not.
            extobj["Not Configured"] = ((row.tagEvidence || {}).notConfigured || []).join("; ");
            extobj["Cannot Verify"] = ((row.tagEvidence || {}).notObservable || []).join("; ");
            extobj["Missing Tags"] = (row.missingTags || []).join("; ");
            extobj["Matched Tags"] = (row.matchedTags || []).join("; ");
            // Config determination, in the same negative-before-positive order: what the check
            // could not establish matters more than what it confirmed. Matched lines arrive already
            // redacted from the backend — and a raw CLI line is exactly what CsvSerializer's
            // formula guard exists for, since one can begin with "-" or contain "|".
            extobj["Config Checks"] = (row.configEvidence || [])
                .map((check) => check.command || check.pattern)
                .join("; ");
            extobj["Config Result"] = (row.configEvidence || [])
                .map((check) => check.outcome)
                .join("; ");
            extobj["Config Evidence"] = (row.configEvidence || [])
                .map((check) =>
                    (check.matchedLines || [])
                        .map((line) => "L" + line.lineNumber + ": " + line.text)
                        .join(" ")
                )
                .filter(Boolean)
                .join("; ");
            extobj["Advisory ID"] = row.advisoryId;
            extobj["Advisory Title"] = advisory.advisoryTitle;
            extobj["First Fixed"] = advisory.firstFixed;
            extobj["Contributing Advisories"] = (row.contributingAdvisoryIds || []).join("; ");
            extobj["Qualified At"] = this._toLocaleString(row.qualifiedAt);
            extobj["Run ID"] = row.runId;
            extobj["Rule Version"] = row.ruleVersionAtRun;
            extobj["Device Version At Run"] = row.deviceVersionAtRun;
            extobj["Snapshot At Run"] = row.snapshotIdAtRun;
            extobj["Signature Hash At Run"] = row.signatureHashAtRun;
            extobj["Device OS At Run"] = row.deviceOsAtRun;
            // ── Exposure rule (the LLM-extracted predicate that scored this row) ──
            const rule = rulesByAdvisoryId.get(row.advisoryId) || {};
            const clause = this._ruleClauseForOs(rule, row.deviceOsAtRun);
            const extraction = rule.extraction || {};
            const usage = extraction.usage || {};
            extobj["Rule Status"] = rule.status;
            extobj["Current Rule Version"] = rule.version;
            // Predicate-level, not per-clause: the platform gate is evaluated once against the
            // whole advisory.
            //
            // READ THIS BEFORE TRUSTING THIS COLUMN AS AUDIT EVIDENCE. It reports what the LLM
            // transcribed from the advisory prose, which is NOT always what decided the verdict:
            //
            //   - BLANK MEANS "APPLIES TO ALL PLATFORMS", not "not recorded". The backend
            //     contract is explicit that empty/absent means platform-agnostic, i.e. no gate.
            //     Hence the header text — a reader opening the file in a spreadsheet gets that
            //     caveat, where a comment here would reach only us.
            //   - A POPULATED LIST MAY HAVE BEEN IGNORED. When vendor-published CPE hardware says
            //     the advisory is unrestricted, the scorer suppresses this transcribed gate
            //     entirely and never consults it.
            //   - IT CANNOT EXPLAIN A SILENT GATE. NOT_APPLICABLE_PLATFORM is decided from the
            //     authoritative hardware list, which this export does not carry.
            //
            // Exporting the authoritative list beside this one, with empty distinguished from
            // absent, is what would make the pair genuinely auditable.
            extobj["Rule Affected Platforms (blank = all platforms)"] = (
                (rule.predicate || {}).affectedPlatforms || []
            ).join("; ");
            extobj["Rule Required Tags"] = clause.tags;
            extobj["Rule Affected Versions"] = clause.versions;
            extobj["Rule Runtime Keywords"] = clause.runtimeKeywords;
            extobj["Rule Show Command"] = clause.showCommand;
            extobj["Rule Active Pattern"] = clause.activePattern;
            // The evidence actually collected on THIS device: the show command
            // run, how its output was interpreted (active/clear/failed), and
            // the output excerpt or failure reason behind that interpretation.
            const evidence = row.runtimeEvidence || [];
            extobj["Runtime Command Run"] = evidence
                .map((ev) => ev.command)
                .filter(Boolean)
                .join("; ");
            extobj["Runtime Result"] = evidence
                .map((ev) => ev.interpretation)
                .filter(Boolean)
                .join("; ");
            extobj["Runtime Output"] = evidence
                .map((ev) => ev.failureReason || ev.outputExcerpt || "")
                .filter(Boolean)
                .join(" | ")
                .replace(/[\r\n]+/g, " ");
            extobj["Extracted At"] = rule.extractedAt ? this._toLocaleString(rule.extractedAt) : "";
            extobj["Extracted By Model"] = rule.extractedByModel;
            extobj["LLM Connection"] = rule.llmConnectionId;
            extobj["Advisory Source"] = rule.advisorySource;
            extobj["Prompt Version"] = extraction.promptVersion;
            extobj["Input Tokens"] = usage.inputTokens;
            extobj["Output Tokens"] = usage.outputTokens;
            extobj["Extraction Latency (ms)"] = extraction.latencyMs;
            extobj["Extraction Error"] = (rule.extractionError || {}).message;
            printdata.push(extobj);
        });
        const content = this.model.gameEditor.convertToCSV(printdata);
        if (content) {
            this.model.gameEditor.saveCSVFile(fileName, content);
        } else {
            const feedback = this.model.getUserFeedback();
            if (feedback) {
                feedback.displayError({
                    errorMsg: printdata.length
                        ? "The exposure export could not be generated."
                        : "No CVE qualification results to export for this device.",
                    errorCode: "",
                });
            }
        }
        this.model.gameEditor.enableViewAction("DownloadExposure");
        return this;
    }

    /**
     * Export view of the predicate clause that scored this row: the clause
     * whose os matches deviceOsAtRun. Older rows predate the deviceOsAtRun
     * stamp — those fall back to a per-clause summary so the export stays
     * truthful instead of guessing a clause. All values return as CSV-safe
     * strings ("; "-joined).
     */
    _ruleClauseForOs(rule, deviceOsAtRun) {
        const empty = {
            tags: "",
            versions: "",
            runtimeKeywords: "",
            showCommand: "",
            activePattern: "",
        };
        const clauses = (rule && rule.predicate && rule.predicate.clauses) || [];
        if (!clauses.length) {
            return empty;
        }
        const os = (deviceOsAtRun || "").toLowerCase();
        const clause = os ? clauses.find((c) => (c.os || "").toLowerCase() === os) : undefined;
        if (clause) {
            const show = clause.runtimeShow || {};
            const showCommand =
                show.command || (Array.isArray(show.commands) ? show.commands.join("; ") : "");
            return {
                tags: (clause.featureTagsRequired || []).join("; "),
                versions: (clause.affectedVersions || []).join("; "),
                runtimeKeywords: (clause.runtimeKeywordsFound || clause.runtimeKeywords || []).join(
                    "; "
                ),
                showCommand: String(showCommand),
                activePattern: String(show.activePattern || ""),
            };
        }
        // No stamp (or no matching clause): summarize every clause compactly.
        const summary = clauses
            .map(
                (c) =>
                    `${c.os || "?"}: ${(c.featureTagsRequired || []).length} tags ` +
                    `${(c.affectedVersions || []).length} versions`
            )
            .join(" | ");
        return {
            tags: summary,
            versions: summary,
            runtimeKeywords: "",
            showCommand: "",
            activePattern: "",
        };
    }

    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderQualification(qualification) {
        return Promise.resolve().then(() => {
            const advisoriesById = this._model.getAdvisoriesById() || new Map();
            while (this._rootElem.firstChild) {
                this._rootElem.removeChild(this._rootElem.firstChild);
            }
            const lifecycle = this._buildLifecycleBanner(qualification);
            if (lifecycle) {
                this._rootElem.appendChild(lifecycle);
            }
            this._rootElem.appendChild(this._buildSummaryStrip(qualification));
            this._rootElem.appendChild(
                this._buildQualificationTable(qualification, advisoriesById)
            );
            // Both need the table measured, so both run after it is in the document.
            this._applyDescriptionTooltips();
            this._restoreListPosition();
        });
    }

    /**
     * Per-dependency freshness chips (Config / Taxonomy / Advisories /
     * Qualified) as an inline strip sharing the summary row — right-aligned
     * after the verdict tiles, so freshness and counts read as one line.
     * Each chip: status dot (green fresh / amber stale / gray unknown), label,
     * compact age (or taxonomy version); the absolute timestamp and the
     * fail-closed note live in the tooltip.
     * @param {Object} qualification Pristine DeviceCveQualification props.
     * @returns {HTMLElement}
     */
    _buildFreshnessBar(qualification) {
        const now = Date.now();
        const bar = document.createElement("exposure-freshness-bar");
        const chips = computeDependencyFreshness(
            qualification.depFreshness || null,
            this._model.getFreshnessPrefs(),
            now
        );
        chips.forEach((chip) => {
            const chipElem = document.createElement("exposure-freshness-chip");
            chipElem.setAttribute("for", chip.status);
            const dot = document.createElement("span");
            dot.className = "freshness-dot";
            chipElem.appendChild(dot);
            const label = document.createElement("span");
            label.className = "freshness-chip-label";
            label.textContent = chip.label;
            chipElem.appendChild(label);
            const valueText = this._freshnessChipValue(chip, now);
            if (valueText) {
                const value = document.createElement("span");
                value.className = "freshness-chip-value";
                value.textContent = valueText;
                chipElem.appendChild(value);
            }
            const titleParts = [];
            if (chip.detail) {
                titleParts.push(chip.detail);
            }
            if (chip.asOf) {
                titleParts.push("as of " + this._toLocaleString(chip.asOf));
            }
            if (chip.status === "unknown") {
                titleParts.push("freshness cannot be verified — treat as stale");
            }
            chipElem.title = chip.label + ": " + (titleParts.join(" · ") || chip.status);
            bar.appendChild(chipElem);
        });

        return bar;
    }

    /**
     * The chip's value token: compact age for time-based dimensions, an em
     * dash for unknown. Taxonomy carries NO token — it is version drift, not
     * age, and the versions live in the tooltip; the status word is the dot.
     * @param {Object} chip One computeDependencyFreshness() entry.
     * @param {Number} now Epoch ms.
     * @returns {String} empty string when the chip renders label-only
     */
    _freshnessChipValue(chip, now) {
        if (chip.key === "taxonomy") {
            return "";
        }
        if (chip.status === "unknown") {
            return "—";
        }
        return formatAgeShort(chip.asOf, now) || "—";
    }

    /**
     * Puts the operator back where they were in the list.
     *
     * Two steps, in this order, because they answer different questions. The offset restores the
     * exact view they left, which is what "back" should mean for a CVE they opened and closed. The
     * row check then covers stepping: someone who walked from CVE 3 to CVE 30 inside the drawer left
     * an offset that no longer shows the CVE they are actually reading, so the row they ended on is
     * centred instead. When they never moved, the row is already on screen and nothing shifts.
     *
     * Scrolling is done by adjusting the container's own offset rather than through
     * Element.scrollIntoView, which walks every scrollable ancestor and would drag the whole editor
     * pane around to satisfy a request about one table.
     */
    _restoreListPosition() {
        const remembered = ExposureNavMemory.recall(this._model.getDeviceId());
        if (remembered.scrollTop > 0) {
            this._rootElem.scrollTop = remembered.scrollTop;
        }
        if (!remembered.focusCveId) {
            return;
        }
        const row = this._rootElem.querySelector('tr[data-cve-id="' + remembered.focusCveId + '"]');
        if (!row) {
            return;
        }
        // Marks where reading stopped. Without it, a list that scrolled itself to an arbitrary
        // offset reads as a rendering glitch rather than as a resumed position.
        row.classList.add("last-viewed");
        const containerRect = this._rootElem.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        if (rowRect.top < containerRect.top || rowRect.bottom > containerRect.bottom) {
            this._rootElem.scrollTop +=
                rowRect.top - containerRect.top - (containerRect.height - rowRect.height) / 2;
        }
    }

    /**
     * Banner for the device's exposure lifecycle (D9), above the counts it qualifies.
     *
     * The table below always renders the last persisted verdicts, with no indication of whether
     * they are still being recomputed or already known to be out of date. "Last Updated" is a
     * timestamp, not a status: a reader cannot tell a stale result from a current one without
     * knowing the freshness policy. CURRENT gets no banner — the absence of a caveat IS the
     * good state, and a green "everything is fine" bar on every visit trains people to skip the
     * row where the caveat eventually appears.
     *
     * @returns {Element|null} Null when the status is CURRENT, unknown, or unavailable.
     */
    _buildLifecycleBanner(qualification) {
        const lifecycle = this._model.getExposureLifecycle
            ? this._model.getExposureLifecycle()
            : null;
        const status = ((lifecycle && lifecycle.status) || "").trim().toUpperCase();
        // Covers every non-CURRENT member of the Device.exposureStatus enum that can appear with
        // results on screen. UNKNOWN ("never run") is absent on purpose: with no qualification the
        // view has already shown its no-data message and never reaches here.
        const messages = {
            STALE: "These results are out of date — the device configuration or the advisory data has changed since they were computed.",
            ERROR: "The last qualification run failed. These results are from the last run that completed.",
        };
        const activityMessages = {
            QUEUED: "A qualification run is queued for this device. These results are from the previous run.",
            RUNNING: "A qualification run is in progress. These results are from the previous run.",
        };
        const activity = ((lifecycle && lifecycle.activity) || "").trim().toUpperCase();
        const activityOn = lifecycle && lifecycle.activityOn;
        const activityFresh =
            typeof activityOn === "number" &&
            Date.now() - activityOn < EXPOSURE_ACTIVITY_MAX_AGE_MS;
        const runMessage = activityFresh ? activityMessages[activity] : null;
        if (parseHealth((qualification && qualification.depFreshness) || null) === "broken") {
            messages.ERROR =
                "Config facts could not be established for this device — decomposition failed, " +
                "so feature-gated CVEs stay inconclusive. Version-only results below are unaffected.";
        }
        if (!messages[status] && !runMessage) {
            return null;
        }
        const banner = document.createElement("exposure-lifecycle-banner");
        banner.setAttribute("for", (messages[status] ? status : activity).toLowerCase());
        // role=status, not alert: this is a standing condition of the page, and an alert would
        // interrupt a screen-reader user mid-sentence every time they open the view.
        banner.setAttribute("role", "status");
        // A run in flight is shown ALONGSIDE the durable caveat, never instead of it: a device
        // whose last qualification failed must keep saying so while the next run is queued.
        if (runMessage) {
            const runText = document.createElement("span");
            runText.textContent = runMessage;
            banner.appendChild(runText);
        }
        if (messages[status]) {
            const text = document.createElement("span");
            text.textContent = messages[status];
            banner.appendChild(text);
        }
        if (lifecycle && lifecycle.evaluatedOn) {
            const evaluated = document.createElement("span");
            evaluated.className = "lifecycle-evaluated";
            evaluated.textContent = "Last evaluated " + this._toLocaleString(lifecycle.evaluatedOn);
            banner.appendChild(evaluated);
        }
        return banner;
    }

    _buildSummaryStrip(qualification) {
        const summary = qualification.summary || {};
        const summaryElem = document.createElement("exposure-summary-gl");

        SUMMARY_ITEMS.forEach((item) => {
            const count = summary[item.key] || 0;
            const countElem = document.createElement("exposure-summary-count");
            countElem.setAttribute("for", item.score);
            if (count === 0) {
                countElem.classList.add("empty");
            }
            const value = document.createElement("span");
            value.className = "summary-count-value";
            value.textContent = String(count);
            countElem.appendChild(value);
            const label = document.createElement("span");
            label.className = "summary-count-label";
            label.textContent = item.label;
            countElem.appendChild(label);
            summaryElem.appendChild(countElem);
        });

        // Same row, right-aligned: the compact tiles freed the horizontal
        // space, so freshness shares the line instead of costing height.
        summaryElem.appendChild(this._buildFreshnessBar(qualification));

        return summaryElem;
    }

    /*******************************************/
    /* START QUALIFICATION TABLE METHODS       */
    /*******************************************/

    /**
     * Default ordering: Affected first, then Potentially Affected, Inconclusive,
     * Not Affected; within a disposition, higher severity first; ties broken by
     * CVE id for a stable order.
     */
    _sortQualifications(rows, advisoriesById) {
        const dispositionRank = (score) => {
            const rank = DISPOSITION_SORT_RANK[(score || "").toLowerCase()];
            return rank === undefined ? 99 : rank;
        };
        const severityRank = (advisoryId) =>
            this._severityInfo((advisoriesById.get(advisoryId) || {}).sir).rank;
        return rows.slice().sort((a, b) => {
            const dispDiff = dispositionRank(a.score) - dispositionRank(b.score);
            if (dispDiff !== 0) {
                return dispDiff;
            }
            const sevDiff = severityRank(b.advisoryId) - severityRank(a.advisoryId);
            if (sevDiff !== 0) {
                return sevDiff;
            }
            return (a.cveId || "").localeCompare(b.cveId || "");
        });
    }

    _buildQualificationTable(qualification, advisoriesById) {
        const qualifications = this._sortQualifications(
            qualification.qualifications || [],
            advisoriesById
        );
        const tableContainer = document.createElement("exposure-table-gl");
        const table = document.createElement("table");

        const thead = document.createElement("thead");
        const headerRow = document.createElement("tr");
        TABLE_HEADERS.forEach((header) => {
            const th = document.createElement("th");
            th.textContent = header;
            headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        const tbody = document.createElement("tbody");
        qualifications.forEach((row) => {
            tbody.appendChild(this._buildQualificationRow(row, advisoriesById));
        });
        table.appendChild(tbody);

        // The drawer steps in THIS order rather than deriving its own: the sequence an operator
        // works through is the one they can see, and severity ranking here depends on advisory
        // metadata the drawer only loads for a single CVE.
        ExposureNavMemory.remember(this._model.getDeviceId(), {
            order: qualifications.map((row) => row.cveId).filter(Boolean),
        });

        tableContainer.appendChild(table);
        return tableContainer;
    }

    _buildQualificationRow(row, advisoriesById) {
        const tr = document.createElement("tr");
        const advisory = advisoriesById.get(row.advisoryId) || {};
        if (row.cveId) {
            // Lets a rebuilt table find the row the operator was last reading.
            tr.dataset.cveId = row.cveId;
        }

        // The CVE id IS the link to the drawer (D6). Keyboard focus belongs on one small named
        // target, not on the whole row: a focused <tr> makes a screen reader announce every cell
        // as the accessible name of a single "button", so the user hears the entire row read out
        // before learning what activating it does.
        const cveCell = document.createElement("td");
        if (row.cveId) {
            const cveLink = document.createElement("a");
            cveLink.className = "cve-link";
            cveLink.textContent = row.cveId;
            // Not an href: this navigates within the editor, and a real href would offer
            // open-in-new-tab on a URL that does not resolve. role+tabindex give the anchor the
            // semantics it loses without one.
            cveLink.setAttribute("role", "link");
            cveLink.setAttribute("tabindex", "0");
            cveLink.setAttribute("aria-label", "View details for " + row.cveId);
            cveCell.appendChild(cveLink);
        } else {
            cveCell.textContent = "";
        }
        tr.appendChild(cveCell);

        // Disposition chip: presentation-layer vocabulary, existing score coloring.
        //
        // The qualifier rides OUTSIDE the chip. Inside, it would be color-coded red at the same
        // weight as the verdict itself and read as part of the severity; outside and subdued, it
        // reads as what it is — a note on how much the verdict is worth. On the reference device
        // 21 of 22 reds are version-only matches, so without this the column shows 22 identical
        // "Affected" chips for one config-confirmed finding and 21 that never saw a config.
        const dispositionCell = document.createElement("td");
        const dispositionElem = document.createElement("exposure-score");
        dispositionElem.setAttribute("for", (row.score || "").toLowerCase());
        dispositionElem.textContent = ExposureDispositions.dispositionLabel(row.score);
        dispositionCell.appendChild(dispositionElem);
        const qualified = ExposureDispositions.dispositionLabel(row.score, row.reasonCode);
        const unqualified = ExposureDispositions.dispositionLabel(row.score);
        if (qualified !== unqualified) {
            const qualifier = document.createElement("span");
            // Weight class as well as the base: three of the four qualifiers STRENGTHEN the chip,
            // and they were being painted in the muted treatment meant for the one that weakens it.
            qualifier.className =
                "disposition-qualifier " + ExposureDispositions.qualifierWeight(row.reasonCode);
            qualifier.textContent = qualified.slice(unqualified.length).trim();
            // Tooltip from the vocabulary owner, not a local copy. The hardcoded version-only
            // wording here went wrong the moment config determination added qualifiers meaning the
            // OPPOSITE, and a local map is how the drawer ended up with no tooltip at all.
            qualifier.setAttribute("title", ExposureDispositions.qualifierTooltip(row.reasonCode));
            dispositionCell.appendChild(qualifier);
        }
        tr.appendChild(dispositionCell);

        // Which check decided this verdict. `decidedAt` is already stored on every entry and
        // already maps through DECIDED_AT_LABELS, so this is a column definition rather than a
        // feature — and without it a config-confirmed red is indistinguishable from a version-only
        // one across a 97-row table, which is where an operator actually triages.
        const decidedCell = document.createElement("td");
        decidedCell.textContent = ExposureDispositions.decidedAtLabel(row.decidedAt);
        tr.appendChild(decidedCell);

        const severityCell = document.createElement("td");
        const severity = this._severityInfo(advisory.sir);
        const severityBadge = document.createElement("exposure-severity");
        severityBadge.setAttribute("for", severity.token);
        if (severity.rank > 0) {
            // Mirror the Advisory Summary criticality rendering: warning icon
            // colored by the same for-attribute severity tokens, plus the label.
            const severityIcon = document.createElement("actionable-icon-gl");
            severityIcon.dataset.icon = "common.warning";
            severityBadge.appendChild(severityIcon);
        }
        severityBadge.appendChild(document.createTextNode(severity.label));
        severityCell.appendChild(severityBadge);
        tr.appendChild(severityCell);

        // Description: the advisory title on one line, with the rest of the story in a tooltip.
        // The summary is the prose the removed hover popup used to carry — it is the only place a
        // reader learns what the vulnerability actually IS without opening the drawer, so it rides
        // the tooltip rather than disappearing with the popup.
        const advisoryCell = document.createElement("td");
        advisoryCell.className = "advisory-cell";
        const advisoryTitle = advisory.advisoryTitle || "—";
        advisoryCell.textContent = advisoryTitle;
        const summary = advisory.summary
            ? this._plainText(advisory.summary, TOOLTIP_SUMMARY_LIMIT)
            : "";
        if (summary) {
            advisoryCell.setAttribute("title", advisoryTitle + "\n\n" + summary);
        } else {
            // No summary to add, so the tooltip is only worth having if the title is clipped —
            // decided after layout, by _applyDescriptionTooltips.
            advisoryCell.dataset.fullText = advisoryTitle;
        }
        tr.appendChild(advisoryCell);

        const reasonCell = document.createElement("td");
        const reasonText = document.createElement("span");
        reasonText.textContent = row.reasonText || "";
        reasonCell.appendChild(reasonText);
        if (row.reasonCode) {
            reasonCell.setAttribute("title", row.reasonCode);
        }
        tr.appendChild(reasonCell);

        // Row click opens the per-CVE detail drawer (mirrors the Advisory
        // Summary subview pattern), with a breadcrumb back to the Exposure view.
        if (row.cveId) {
            tr.classList.add("clickable");
            tr.setAttribute("title", "View CVE details");
            const open = () => this._openExposureDetails(row.cveId);
            // Mouse: anywhere on the row, which is the larger and more forgiving target.
            tr.addEventListener("click", open);
            // Keyboard: the CVE link only. The drawer is the ONLY route to the tag-evidence
            // buckets and the deciding level — the hover popup needs a mouse-over, which a
            // keyboard or touch user does not have. Without a focusable target a screen-reader
            // user could read "Not Affected" with no way to reach the evidence behind it, which
            // on a security surface is the difference between a verdict and a claim.
            const cveLink = tr.querySelector(".cve-link");
            if (cveLink) {
                cveLink.addEventListener("keydown", (event) => {
                    if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
                        // Space would otherwise scroll the page out from under the row.
                        event.preventDefault();
                        open();
                    }
                });
                // The row handler already fires for a click that lands on the link; without this
                // the drawer would open, close and re-open from one activation.
                cveLink.addEventListener("click", (event) => event.stopPropagation());
                cveLink.addEventListener("click", open);
            }
        }

        return tr;
    }

    /**
     * Advisory summaries are frequently HTML — reduce to readable plain text
     * (and cap the length) before putting them in a tooltip.
     */
    _plainText(value, maxLength) {
        // DOMParser produces an inert document: no resource loading and no
        // event handlers fire while reducing untrusted HTML to text.
        const parsed = new DOMParser().parseFromString(String(value), "text/html");
        const text = ((parsed.body && parsed.body.textContent) || "").replace(/\s+/g, " ").trim();
        if (maxLength && text.length > maxLength) {
            return text.slice(0, maxLength) + "…";
        }
        return text;
    }

    /**
     * Gives a tooltip to the Description cells whose text does not fit.
     *
     * Only the clipped ones: a tooltip that repeats a line already fully on screen is noise, and
     * once every row has one the reader stops expecting them to carry anything. Cells whose
     * advisory has a summary were given a tooltip at build time — that one adds prose the column
     * never shows, so it earns its place whether or not the title fits.
     *
     * Runs after the table is in the document, since it needs measured widths.
     */
    _applyDescriptionTooltips() {
        const cells = this._rootElem.querySelectorAll(".advisory-cell[data-full-text]");
        Array.prototype.forEach.call(cells, (cell) => {
            if (cell.scrollWidth > cell.clientWidth) {
                cell.setAttribute("title", cell.dataset.fullText);
            }
        });
    }

    _openExposureDetails(cveId) {
        // Stamped here rather than left to the scroll handler: a row opened without the list ever
        // being scrolled (the first screen) has fired no scroll event at all.
        ExposureNavMemory.remember(this._model.getDeviceId(), {
            scrollTop: this._rootElem.scrollTop,
            focusCveId: cveId,
        });
        this._model.gameEditor.setActiveView(
            Registrations.DIE_VIEW_EXPOSURE_DETAILS,
            [
                {
                    value: {
                        fqn: Registrations.DIE_VIEW_EXPOSURE,
                    },
                    content: "Exposure",
                    left: {
                        arrow: {
                            direction: "left",
                        },
                    },
                },
            ],
            {
                cveId: cveId,
            }
        );
    }

    /*******************************************/
    /* END QUALIFICATION TABLE METHODS         */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    /** Normalize a Cisco SIR string into a display label, css token, and rank. */
    _severityInfo(sir) {
        const normalized = (sir || "").trim().toLowerCase();
        return {
            normalized: normalized,
            token: normalized || "none",
            label: sir ? String(sir) : "—",
            rank: SEVERITY_RANK[normalized] || 0,
            isHigh: normalized === "critical" || normalized === "high",
        };
    }

    _toLocaleString(val) {
        const d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = ExposureViewRenderer;
